// Вход в Mini App: экрана входа нет, данные запуска меняются на токен при каждом открытии.
// Токен хранится только в памяти вкладки.
import { useEffect, useState, type ReactNode } from "react";
import { setMiniAppSessionToken } from "../api/client";
import { loginMiniApp } from "../api/endpoints";
import type { TelegramWebApp } from "./telegramTypes";

type SessionState = "signing_in" | "signed_in" | "failed";

// Одни данные запуска — один вход: сервер не принимает их повторно, а в режиме разработки
// React запускает эффект дважды.
const logins = new Map<string, ReturnType<typeof loginMiniApp>>();

function loginOnce(initData: string): ReturnType<typeof loginMiniApp> {
  let login = logins.get(initData);
  if (login === undefined) {
    login = loginMiniApp(initData);
    logins.set(initData, login);
  }
  return login;
}

// onOpenPath — куда открыть Mini App после входа: заказ из ссылки или последний незаконченный
// (telegram-continuity, решение 3). Сервер решает сам по подписанным данным запуска.
// «Входим через Telegram…» остаётся, пока переход не закончится: экраны заказа грузятся
// отдельным файлом (изменение seo), и без ожидания на миг мелькала развилка.
export function MiniAppSession({
  webApp,
  children,
  onOpenPath,
}: {
  webApp: TelegramWebApp;
  children: ReactNode;
  onOpenPath?: (path: string) => Promise<void> | void;
}) {
  const [state, setState] = useState<SessionState>("signing_in");

  useEffect(() => {
    let cancelled = false;
    loginOnce(webApp.initData)
      .then(async (response) => {
        setMiniAppSessionToken(response.token);
        if (cancelled) return;
        if (response.open_path) {
          // Не открылся заказ (нет связи) — всё равно пускаем в приложение: развилка лучше пустоты.
          await Promise.resolve(onOpenPath?.(response.open_path)).catch(() => undefined);
        }
        if (!cancelled) setState("signed_in");
      })
      .catch(() => {
        if (!cancelled) setState("failed");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- вход один раз на данные запуска
  }, [webApp]);

  if (state === "signing_in") {
    return <p role="status">Входим через Telegram…</p>;
  }
  return (
    <>
      {state === "failed" && (
        <p role="alert">Не получилось войти через Telegram. Закрой и открой приложение снова.</p>
      )}
      {children}
    </>
  );
}
